import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useStars } from '../context/StarContext';
import StillButton from './ui/StillButton';
import { useOverlay } from '../App';
import { useCustomNavigation } from '../hooks/useCustomNavigation';

const FindStarOverlay = () => {
    const navigate = useNavigate();
    const { navigateTo } = useCustomNavigation();
    const { findStar, loading, error: starError, setError } = useStars();
    const { toggleOverlay } = useOverlay();
    const [searchTerm, setSearchTerm] = useState('');
    const [errorMessage, setErrorMessage] = useState(null);

    // Hide the header when this component mounts
    useEffect(() => {
        toggleOverlay(false);
        
        // Clean up when unmounting
        return () => toggleOverlay(false);
    }, [toggleOverlay]);

    // Reset error on mount
    useEffect(() => {
        setError(null);
        return () => setError(null);
    }, [setError]);

    useEffect(() => {
        if (starError) {
            setErrorMessage(starError || 'An unknown error occurred');
            const timer = setTimeout(() => {
                setErrorMessage(null);
            }, 5000);

            return () => clearTimeout(timer);
        }
    }, [starError]);

    const handleClose = () => {
        navigate('/space');
    };

    const handleSearch = async () => {
        if (!searchTerm.trim()) return;

        try {
            const star = await findStar(searchTerm);
            if (star) {
                // Use our custom navigation with transition videos
                navigateTo(`/view-star/${star.usid}`);
            }
        } catch (err) {
            setError(err.message);
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 1,
                staggerChildren: 0.5,
            },
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 5 },
        visible: { 
            opacity: 1, 
            y: 0,
            transition: {
                duration: 2,
                ease: "easeOut"
            }
        }
    };

    return (
        <div className="fixed inset-0 flex flex-col bg-black bg-opacity-40">
            <motion.div 
                className="h-full w-full flex flex-col items-center justify-center p-4"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                <motion.div 
                    className="w-full flex flex-col justify-center px-4 lg:p-10 max-w-xl"
                    variants={containerVariants}
                >
                    <motion.div 
                        className="space-y-6"
                        variants={itemVariants}
                    >
                        <div>
                            <h1 className="mb-2">
                                Find a <span className="text-still-white text-glow-still">Star</span>
                            </h1>
                            <p className="text-still-white text-lg lg:text-lg">
                                Enter a unique star ID to see the love that still shines.
                            </p>
                        </div>

                        {errorMessage && (
                            <motion.div 
                                className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded"
                                variants={itemVariants}
                            >
                                {errorMessage}
                            </motion.div>
                        )}

                        <motion.div 
                            className="space-y-6"
                            variants={itemVariants}
                        >
                            <div>
                                <input
                                    type="text"
                                    placeholder="Enter Star ID (e.g. name-yyyy-mm-dd-city)"
                                    value={searchTerm}
                                    spellCheck="false"
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="p-4 bg-transparent border border-still-blue outline-glow-blue rounded-full w-full text-cyan-300 placeholder-cyan-300/50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 uppercase"
                                />
                            </div>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </motion.div>
            
            {/* Buttons fixed at the bottom */}
            <motion.div 
                className="fixed bottom-0 left-0 right-0 flex justify-center space-x-4 pointer-events-auto mb-20 sm:mb-16 md:mb-20 lg:mb-24 z-50"
                variants={itemVariants}
            >
                <StillButton  
                    onClick={handleSearch}
                    disabled={loading}
                    aria-label="Search"
                    className="w-auto"
                >
                    {loading ? 'SEARCHING...' : 'SEARCH'}
                </StillButton>
                <StillButton 
                    onClick={handleClose}
                    aria-label="Close"
                    className="w-auto"
                >
                    CLOSE
                </StillButton>
            </motion.div>
        </div>
    );
};

export default FindStarOverlay; 